import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';
import WhatsappSlipButton from '../../component/WhatsappSlipButton';
import PracharButton from '../../component/PracharButton';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothCaptainVoter = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appId, language, userId, userName } = useAppId();

    const boothNo = location.state?.boothId;
    const lang = language || 'en';

    // Filter state for voters
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'available', 'not_available', 'pending'
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

    // 🔴 NEW: Search state for voter section
    const [voterSearchTerm, setVoterSearchTerm] = useState("");

    // Helper function to convert Status 'D', 'C', 'P', 'N' to Full Form
    const getStatusFullForm = (statusCode) => {
        switch (statusCode?.toUpperCase()) {
            case 'D': return 'Doubtful';
            case 'C': return "Can't Say";
            case 'P': return 'Positive';
            case 'N': return 'Negative';
            default: return statusCode || 'Voted';
        }
    };

    // 🔴 NEW: Helper function to determine the color class based on the voter status
    const getStatusColorClass = (statusCode) => {
        switch (statusCode?.toUpperCase()) {
            case 'P': return 'status-green';
            case 'N': return 'status-red';
            case 'D': return 'status-orange';
            case 'C': return 'status-blue';
            default: return 'status-green'; // fallback
        }
    };

    // Generate WhatsApp message in Gujarati for Captain
    const generateWhatsAppMessage = (captainName, userType) => {
        const loginUserName = userName || 'Admin';
        const designation = userType === 'BC' ? 'બૂથ કેપ્ટન' : 'વોટર કેપ્ટન';
        return `નમસ્તે ${captainName}, ${loginUserName} એ તમને ${designation}ની જવાબદારી સોંપેલી છે.`;
    };

    // Generate SMS message in Gujarati for Captain
    const generateSMSMessage = (captainName, userType) => {
        const loginUserName = userName || 'Admin';
        const designation = userType === 'BC' ? 'બૂથ કેપ્ટન' : 'વોટર કેપ્ટન';
        return `નમસ્તે ${captainName}, ${loginUserName} એ તમને ${designation}ની જવાબદારી સોંપેલી છે.`;
    };

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Booth Details",
            tabCadre: "Sangathan",
            tabVoter: "Voters",
            boothCaptain: "Booth Captain",
            voterCaptain: "Voter Captain",
            addVoterCaptain: "+ Create Voter Captain",
            editCaptain: "Edit Captain",
            deleteCaptain: "Delete Captain",
            voters: "Voters",
            voterFrom: "Voter No. From",
            voterTo: "Voter No. To",
            totalVoters: "Total Voters",
            noData: "No Voter Captain available.",
            fatherHusband: "F/S :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            address: "Address :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            print: "Print",
            prachar: "Prachar",
            family: "Family",
            addToGroup: "Add to Group",
            active: "Active",
            inactive: "Inactive",
            sendWhatsapp: "Send Responsibility in Whatsapp",
            sendSMS: "Send Responsibility in SMS",
            cancelBtn: "Cancel",
            submitBtn: "Create Captain",
            updateBtn: "Update Captain",
            addingBtn: "Creating...",
            updatingBtn: "Updating...",
            addModalTitle: "Create Voter Captain",
            editModalTitle: "Edit Captain",
            nameLabel: "Full Name",
            mobileLabel: "Mobile Number",
            startVoterLabel: "Start Voter No",
            endVoterLabel: "End Voter No",
            fillAllFields: "Please fill all details",
            validMobile: "Please enter a valid 10-digit mobile number",
            mobileRequired: "Mobile number is required",
            mobileLength: "Mobile number must be 10 digits",
            mobileNumeric: "Mobile number must contain only digits",
            mobileInvalid: "Please enter a valid Indian mobile number (starts with 6,7,8,9)",
            addSuccess: "Captain Created Successfully",
            updateSuccess: "Updated successfully!",
            addError: "Error creating captain",
            updateError: "Error updating captain",
            deleteConfirm: "Are you sure you want to delete this captain?",
            deleteSuccess: "Captain deleted successfully!",
            deleteError: "Error deleting captain",
            addSurvey: "Add Survey",
            filterAll: "All",
            filterAvailable: "Available",
            filterNotAvailable: "Not Available",
            filterPending: "Pending",
            editMobileTitle: "Update Mobile Number",
            mobilePlaceholder: "Enter 10-digit mobile number",
            mobileErrorLength: "Mobile number must be 10 digits",
            mobileErrorStart: "Number must start with 6, 7, 8, or 9",
            cancel: "Cancel",
            save: "Save",
            saving: "Saving...",
            mobileUpdateSuccess: "Mobile number updated successfully!",
            mobileUpdateFailed: "Failed to update mobile number.",
            mobileUpdateError: "An error occurred while updating the mobile number.",
            mobileNotAvailable: "Mobile number is not available for this voter.",
            addressNotAvailable: "Address not available!",
            searchVoter: "Search voter by name, mobile or ID..."
        },
        hi: {
            pageTitle: "बूथ विवरण",
            tabCadre: "संगठन",
            tabVoter: "मतदाता",
            boothCaptain: "बूथ कैप्टन",
            voterCaptain: "वोटर कैप्टन",
            addVoterCaptain: "+ वोटर कैप्टन बनाएं",
            editCaptain: "कैप्टन संपादित करें",
            deleteCaptain: "कैप्टन हटाएं",
            voters: "मतदाता",
            voterFrom: "वोटर नं. से",
            voterTo: "वोटर नं. तक",
            totalVoters: "कुल मतदाता",
            noData: "कोई डेटा उपलब्ध नहीं है।",
            fatherHusband: "पिता/पति :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            address: "पता :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            print: "प्रिन्ट",
            prachar: "प्रचार",
            family: "परिवार",
            addToGroup: "समूह में जोड़ें",
            active: "सक्रिय",
            inactive: "निष्क्रिय",
            sendWhatsapp: "व्हाट्सएप में जिम्मेदारी भेजें",
            sendSMS: "एसएमएस में जिम्मेदारी भेजें",
            cancelBtn: "रद्द करें",
            submitBtn: "कैप्टन बनाएं",
            updateBtn: "अपडेट करें",
            addingBtn: "बनाया जा रहा है...",
            updatingBtn: "अपडेट किया जा रहा है...",
            addModalTitle: "वोटर कैप्टन बनाएं",
            editModalTitle: "कैप्टन संपादित करें",
            nameLabel: "पूरा नाम",
            mobileLabel: "मोबाइल नंबर",
            startVoterLabel: "प्रारंभ वोटर नं",
            endVoterLabel: "अंत वोटर नं",
            fillAllFields: "कृपया सभी विवरण भरें",
            validMobile: "कृपया 10 अंकों का मोबाइल नंबर दर्ज करें।",
            mobileRequired: "मोबाइल नंबर आवश्यक है",
            mobileLength: "मोबाइल नंबर 10 अंकों का होना चाहिए",
            mobileNumeric: "मोबाइल नंबर में केवल अंक होने चाहिए",
            mobileInvalid: "कृपया एक वैध भारतीय मोबाइल नंबर दर्ज करें (6,7,8,9 से शुरू)",
            addSuccess: "कैप्टन सफलतापूर्वक बनाया गया",
            updateSuccess: "सफलतापूर्वक अपडेट किया गया!",
            addError: "कैप्टन बनाने में त्रुटि",
            updateError: "कैप्टन अपडेट करने में त्रुटि",
            deleteConfirm: "क्या आप वाकई इस कैप्टन को हटाना चाहते हैं?",
            deleteSuccess: "कैप्टन सफलतापूर्वक हटा दिया गया!",
            deleteError: "कैप्टन हटाने में त्रुटि",
            addSurvey: "सर्वेक्षण जोड़ें",
            filterAll: "सभी",
            filterAvailable: "उपलब्ध",
            filterNotAvailable: "अनुपलब्ध",
            filterPending: "लंबित",
            editMobileTitle: "मोबाइल नंबर अपडेट करें",
            mobilePlaceholder: "10 अंकों का मोबाइल नंबर दर्ज करें",
            mobileErrorLength: "मोबाइल नंबर 10 अंकों का होना चाहिए",
            mobileErrorStart: "नंबर 6, 7, 8, या 9 से शुरू होना चाहिए",
            cancel: "रद्द करें",
            save: "सेव करें",
            saving: "सेव हो रहा है...",
            mobileUpdateSuccess: "मोबाइल नंबर सफलतापूर्वक अपडेट हो गया!",
            mobileUpdateFailed: "मोबाइल नंबर अपडेट करने में विफल।",
            mobileUpdateError: "मोबाइल नंबर अपडेट करते समय एक त्रुटि हुई।",
            mobileNotAvailable: "इस मतदाता के लिए मोबाइल नंबर उपलब्ध नहीं है।",
            addressNotAvailable: "पता उपलब्ध नहीं है!",
            searchVoter: "नाम, मोबाइल या ID से मतदाता खोजें..."
        },
        gu: {
            pageTitle: "બૂથ વિગતો",
            tabCadre: "સંગઠન",
            tabVoter: "વોટર",
            boothCaptain: "બૂથ કેપ્ટન",
            voterCaptain: "વોટર કેપ્ટન",
            addVoterCaptain: "+ વોટર કેપ્ટન બનાવો",
            editCaptain: "કેપ્ટન સંપાદિત કરો",
            deleteCaptain: "કેપ્ટન કાઢી નાખો",
            voters: "મતદાર",
            voterFrom: "વોટર નં. થી",
            voterTo: "વોટર નં. સુધી",
            totalVoters: "ટોટલ મતદાર",
            noData: "કોઈ માહિતી ઉપલબ્ધ નથી.",
            fatherHusband: "પિતા/પતિ :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            address: "સરનામું :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            print: "પ્રિનટ",
            prachar: "પ્રચાર",
            family: "કુટુંબ",
            addToGroup: "જૂથમાં ઉમેરો",
            active: "સક્રિય",
            inactive: "નિષ્ક્રિય",
            sendWhatsapp: "વોટ્સએપમાં જવાબદારી મોકલો",
            sendSMS: "એસએમએસમાં જવાબદારી મોકલો",
            cancelBtn: "રદ કરો",
            submitBtn: "કેપ્ટન બનાવો",
            updateBtn: "અપડેટ કરો",
            addingBtn: "બનાવી રહ્યા છે...",
            updatingBtn: "અપડેટ થઈ રહ્યું છે...",
            addModalTitle: "વોટર કેપ્ટન બનાવો",
            editModalTitle: "કેપ્ટન સંપાદિત કરો",
            nameLabel: "પૂરું નામ",
            mobileLabel: "મોબાઇલ નંબર",
            startVoterLabel: "શરૂઆત વોટર નં",
            endVoterLabel: "અંત વોટર નં",
            fillAllFields: "કૃપા કરીને બધી વિગતો ભરો",
            validMobile: "કૃપા કરીને 10 અંકનો મોબાઇલ નંબર દાખલ કરો.",
            mobileRequired: "મોબાઇલ નંબર જરૂરી છે",
            mobileLength: "મોબાઇલ નંબર 10 અંકનો હોવો જોઈએ",
            mobileNumeric: "મોબાઇલ નંબરમાં માત્ર અંકો હોવા જોઈએ",
            mobileInvalid: "કૃપા કરીને માન્ય ભારતીય મોબાઇલ નંબર દાખલ કરો (6,7,8,9 થી શરૂ)",
            addSuccess: "કેપ્ટન સફળતાપૂર્વક બનાવ્યો",
            updateSuccess: "સફળતાપૂર્વક અપડેટ થયું!",
            addError: "કેપ્ટન બનાવવામાં ભૂલ",
            updateError: "કેપ્ટન અપડેટ કરવામાં ભૂલ",
            deleteConfirm: "શું તમે ખરેખર આ કેપ્ટનને કાઢી નાખવા માંગો છો?",
            deleteSuccess: "કેપ્ટન સફળતાપૂર્વક કાઢી નાખવામાં આવ્યો!",
            deleteError: "કેપ્ટન કાઢી નાખવામાં ભૂલ",
            addSurvey: "સર્વે ઉમેરો",
            filterAll: "બધા",
            filterAvailable: "ઉપલબ્ધ",
            filterNotAvailable: "અનુપલબ્ધ",
            filterPending: "બાકી",
            editMobileTitle: "મોબાઇલ નંબર અપડેટ કરો",
            mobilePlaceholder: "10 અંકોનો મોબાઇલ નંબર દાખલ કરો",
            mobileErrorLength: "મોબાઇલ નંબર 10 અંકોનો હોવો જોઈએ",
            mobileErrorStart: "નંબર 6, 7, 8, અથવા 9 થી શરૂ થવો જોઈએ",
            cancel: "રદ કરો",
            save: "સેવ કરો",
            saving: "સેવ થઈ રહ્યું છે...",
            mobileUpdateSuccess: "મોબાઇલ નંબર સફળતાપૂર્વક અપડેટ થયો!",
            mobileUpdateFailed: "મોબાઇલ નંબર અપડેટ કરવામાં નિષ્ફળ.",
            mobileUpdateError: "મોબાઇલ નંબર અપડેટ કરતી વખતે એક ભૂલ આવી.",
            mobileNotAvailable: "આ મતદાર માટે મોબાઇલ નંબર ઉપલબ્ધ નથી.",
            addressNotAvailable: "સરનામું ઉપલબ્ધ નથી!",
            searchVoter: "નામ, મોબાઇલ અથવા ID દ્વારા મતદાર શોધો..."
        }
    };

    const txt = t[lang] || t.gu;

    // --- Edit Mobile State ---
    const [isEditMobileOpen, setIsEditMobileOpen] = useState(false);
    const [editingVoter, setEditingVoter] = useState(null);
    const [newMobile, setNewMobile] = useState('');
    const [isSavingMobile, setIsSavingMobile] = useState(false);

    // --- Edit Mobile API Call ---
    const handleUpdateMobile = async () => {
        if (!newMobile || newMobile.length !== 10) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSavingMobile(true);
        try {
            const payload = {
                app_id: appId?.toString(),
                voter_id: editingVoter.idcard_no,
                mobile_no: newMobile,
                modify_by: userId?.toString() || "1"
            };

            const response = await axios.post(`${BASE_URL}/api/upd_voter_mobile`, payload);

            if (response.data.success || response.data.Success === "1") {
                alert("Mobile number updated successfully!");
                setVoterList(prev => prev.map(v =>
                    v.id === editingVoter.id ? { ...v, contact_no: newMobile } : v
                ));
                setIsEditMobileOpen(false);
            } else {
                alert("Failed to update mobile number.");
            }
        } catch (error) {
            console.error("Error updating mobile:", error);
            alert("An error occurred while updating the mobile number.");
        } finally {
            setIsSavingMobile(false);
        }
    };

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('cadre');

    // Data States
    const [boothCaptains, setBoothCaptains] = useState([]);
    const [voterCaptains, setVoterCaptains] = useState([]);
    const [voterList, setVoterList] = useState([]);
    const [filteredVoterList, setFilteredVoterList] = useState([]);

    // --- Profile Detail Modal State ---
    const [selectedCaptain, setSelectedCaptain] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    // --- Add Voter Captain Modal State ---
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addFormData, setAddFormData] = useState({
        name: '',
        mobile_no: '',
        start_voter_no: '',
        end_voter_no: ''
    });
    const [addMobileError, setAddMobileError] = useState("");

    // --- Edit Captain Modal State ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editFormData, setEditFormData] = useState({
        user_id: '',
        name: '',
        mobile_no: '',
        start_voter_no: '',
        end_voter_no: '',
        user_type: ''
    });
    const [editMobileError, setEditMobileError] = useState("");

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isFilterDropdownOpen && !event.target.closest('.filter-dropdown-container')) {
                setIsFilterDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFilterDropdownOpen]);

    // Apply filter to voter list
    useEffect(() => {
        let filtered = [...voterList];
        if (filterStatus !== 'all') {
            if (filterStatus === 'available') {
                filtered = filtered.filter(voter => voter.voter_available === true);
            } else if (filterStatus === 'not_available') {
                filtered = filtered.filter(voter => voter.voter_available === false);
            } else if (filterStatus === 'pending') {
                filtered = filtered.filter(voter => voter.voter_available === null);
            }
        }
        setFilteredVoterList(filtered);
        setCurrentPage(1);
    }, [filterStatus, voterList]);

    useEffect(() => {
        if (!appId || !boothNo) {
            navigate('/dashboard');
            return;
        }

        fetchBoothData();
    }, [appId, boothNo, navigate]);

    const fetchBoothData = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_captain_cadre_with_voter`, {
                app_id: appId,
                booth_no: boothNo
            });

            if (response.data.success && response.data.data) {
                const cadreData = response.data.data.result || [];

                // Split cadre into Booth Captains and Voter Captains
                setBoothCaptains(cadreData.filter(user => user.user_type === 'BC'));
                setVoterCaptains(cadreData.filter(user => user.user_type === 'VC'));

                // 🔴 FIX: Removing duplicate voters coming from the API
                const rawVoters = response.data.data.result2 || [];
                const uniqueVoters = Array.from(new Map(rawVoters.map(item => [item.idcard_no || item.id, item])).values());

                setVoterList(uniqueVoters);
                setFilteredVoterList(uniqueVoters);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error fetching booth data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Get filter button text based on current filter
    const getFilterButtonText = () => {
        switch (filterStatus) {
            case 'available': return txt.filterAvailable;
            case 'not_available': return txt.filterNotAvailable;
            case 'pending': return txt.filterPending;
            default: return txt.filterAll;
        }
    };

    // Mobile number validation function
    const validateMobileNumber = (number) => {
        if (!number) {
            return { isValid: false, message: txt.mobileRequired };
        }
        if (!/^\d{10}$/.test(number)) {
            return { isValid: false, message: txt.mobileLength };
        }
        if (!/^\d+$/.test(number)) {
            return { isValid: false, message: txt.mobileNumeric };
        }
        const firstDigit = number.charAt(0);
        if (!['6', '7', '8', '9'].includes(firstDigit)) {
            return { isValid: false, message: txt.mobileInvalid };
        }
        return { isValid: true, message: "" };
    };

    // Handle Add Voter Captain
    const handleAddVoterCaptain = async (e) => {
        e.preventDefault();

        const mobileValidation = validateMobileNumber(addFormData.mobile_no);
        if (!addFormData.name || !addFormData.mobile_no || !addFormData.start_voter_no || !addFormData.end_voter_no) {
            alert(txt.fillAllFields);
            return;
        }
        if (!mobileValidation.isValid) {
            alert(mobileValidation.message);
            setAddMobileError(mobileValidation.message);
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                app_id: appId,
                name: addFormData.name,
                mobile_no: addFormData.mobile_no,
                booth_no: boothNo,
                user_type: "VC",
                start_voter_no: String(addFormData.start_voter_no),
                end_voter_no: String(addFormData.end_voter_no),
                create_by: userId || "Admin"
            };

            const response = await axios.post(`${BASE_URL}/api/insert_user`, payload);

            if (response.data.success && response.data.data && response.data.data.Success == "1") {
                alert(txt.addSuccess);
                setIsAddModalOpen(false);
                setAddFormData({ name: '', mobile_no: '', start_voter_no: '', end_voter_no: '' });
                setAddMobileError("");
                fetchBoothData();
            } else {
                alert(response.data.data?.Message || "Mobile number already exists or failed to create captain.");
            }
        } catch (error) {
            console.error("Axios Error:", error);
            alert(txt.addError);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle Edit Captain
    const handleEditClick = () => {
        if (selectedCaptain) {
            setEditFormData({
                user_id: selectedCaptain.user_id || selectedCaptain.id,
                name: selectedCaptain.name,
                mobile_no: selectedCaptain.mobile_no,
                start_voter_no: selectedCaptain.start_voter_no || '',
                end_voter_no: selectedCaptain.end_voter_no || '',
                user_type: selectedCaptain.user_type
            });
            setEditMobileError("");
            setIsDetailModalOpen(false);
            setIsEditModalOpen(true);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'mobile_no') {
            const numericValue = value.replace(/[^0-9]/g, '');
            const limitedValue = numericValue.slice(0, 10);
            setEditFormData(prev => ({ ...prev, [name]: limitedValue }));

            if (editMobileError) setEditMobileError("");
            if (limitedValue.length === 10) {
                const validation = validateMobileNumber(limitedValue);
                if (!validation.isValid) {
                    setEditMobileError(validation.message);
                } else {
                    setEditMobileError("");
                }
            } else if (limitedValue.length > 0 && limitedValue.length < 10) {
                setEditMobileError(txt.mobileLength);
            }
        } else {
            setEditFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdateCaptain = async (e) => {
        e.preventDefault();

        if (!editFormData.name || !editFormData.mobile_no) {
            alert(txt.fillAllFields);
            return;
        }

        const mobileValidation = validateMobileNumber(editFormData.mobile_no);
        if (!mobileValidation.isValid) {
            alert(mobileValidation.message);
            setEditMobileError(mobileValidation.message);
            return;
        }

        setIsEditing(true);

        const payload = {
            user_id: editFormData.user_id,
            app_id: appId,
            name: editFormData.name,
            mobile_no: editFormData.mobile_no,
            user_type: editFormData.user_type,
            booth_no: boothNo,
            create_by: userId || "Admin"
        };

        if (editFormData.user_type === 'VC') {
            payload.start_voter_no = String(editFormData.start_voter_no || '');
            payload.end_voter_no = String(editFormData.end_voter_no || '');
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/update_user`, payload);

            if (response.data.success && response.data.data && response.data.data.Success == "1") {
                alert(txt.updateSuccess);
                setIsEditModalOpen(false);
                setEditMobileError("");
                fetchBoothData();
                setSelectedCaptain(null);
            } else {
                alert(response.data.data?.Message || txt.updateError);
            }
        } catch (error) {
            console.error("Axios Error:", error);
            alert(txt.updateError);
        } finally {
            setIsEditing(false);
        }
    };

    // Handle Delete Captain
    const handleDeleteCaptain = async (id) => {
        if (!id) {
            alert("Error: User ID is missing.");
            return;
        }

        if (window.confirm(txt.deleteConfirm)) {
            try {
                const response = await axios.post(`${BASE_URL}/api/dlt_user`, {
                    app_id: appId,
                    user_id: id,
                    delete_by: userId || "Admin"
                });

                if (response.data.success || response.data.Success == "1" || (response.data.data && response.data.data.Success == "1")) {
                    alert(txt.deleteSuccess);
                    setIsDetailModalOpen(false);
                    setSelectedCaptain(null);
                    fetchBoothData();
                } else {
                    alert(response.data.message || response.data.Message || response.data.data?.Message || "Failed to delete captain.");
                }
            } catch (error) {
                console.error("Delete Error:", error);
                alert(txt.deleteError);
            }
        }
    };

    // --- Pagination Logic ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentVoters = filteredVoterList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoterList.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    // --- Add to Group API Call ---
    const handleAddToGroup = async (voter) => {
        try {
            const payload = {
                app_id: appId?.toString(),
                user_id: userId?.toString() || appId?.toString(),
                idcard: voter.idcard_no
            };
            const response = await axios.post(`${BASE_URL}/api/ins_my_group`, payload);
            if (response.data.success) {
                alert("Added to group successfully!");
            } else {
                alert("Failed to add to group.");
            }
        } catch (error) {
            console.error("Error adding to group:", error);
            alert("An error occurred while adding to the group.");
        }
    };

    // --- WhatsApp Message Generation Logic ---
    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('સ્રી', 'સ્ત્રી')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `મતદાન તા. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;

                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');

            } else {
                alert("Could not fetch family details.");
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
            alert("Error generating message.");
        }
    };

    // --- SMS Message Generation Logic ---
    const handleSmsShare = async (voter) => {
        if (!voter.contact_no) {
            alert("Mobile number not available for this voter.");
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_sms`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: userId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";

                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('સ્રી', 'સ્ત્રી')}\n`;
                    message += `${member.idcard_no}\n`;
                    message += `મતદાન સ્થળ : ${member.eng_polling_location}\n`;
                    message += `મતદાન તા. : ૨૬-૦૪-૨૦૨૬, રવિવાર \n સમય : સવારે ૭.૦૦ થી સાંજે ૬.૦૦ સુધી\n\n`;

                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');

                window.location.href = `sms:${mobile}?body=${encodedMessage}`;

            } else {
                alert("Could not fetch family details for SMS.");
            }
        } catch (error) {
            console.error("Error generating SMS slip:", error);
            alert("Error generating SMS message.");
        }
    };

    // --- Open Detail Modal ---
    const openDetailModal = (captain, event) => {
        if (event) {
            event.stopPropagation();
        }
        setSelectedCaptain(captain);
        setIsDetailModalOpen(true);
    };

    // --- Close Detail Modal ---
    const closeDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedCaptain(null);
    };

    // Filter voters based on search term
    const getFilteredVoters = () => {
        let filtered = [...filteredVoterList];

        if (voterSearchTerm.trim()) {
            const lowerSearch = voterSearchTerm.toLowerCase().trim();
            filtered = filtered.filter(voter =>
                (voter.eng_f_name && voter.eng_f_name.toLowerCase().includes(lowerSearch)) ||
                (voter.eng_surname && voter.eng_surname.toLowerCase().includes(lowerSearch)) ||
                (voter.f_name && voter.f_name.toLowerCase().includes(lowerSearch)) ||
                (voter.surname && voter.surname.toLowerCase().includes(lowerSearch)) ||
                (voter.full_name && voter.full_name.toLowerCase().includes(lowerSearch)) ||
                (voter.eng_full_name && voter.eng_full_name.toLowerCase().includes(lowerSearch)) ||
                (voter.contact_no && voter.contact_no.includes(lowerSearch)) ||
                (voter.idcard_no && voter.idcard_no.toLowerCase().includes(lowerSearch))
            );
        }

        return filtered;
    };

    const searchedVoterList = getFilteredVoters();
    const currentSearchedVoters = searchedVoterList.slice(indexOfFirstItem, indexOfLastItem);
    const searchedTotalPages = Math.ceil(searchedVoterList.length / itemsPerPage);

    return (
        <div className="booth-detail-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={`${txt.pageTitle} - ${boothNo}`} />
            </div>

            {/* Sticky Tabs */}
            <div className="tabs-container shadow-sm">
                <div
                    className={`tab-btn ${activeTab === 'cadre' ? 'active text-danger' : 'text-muted'}`}
                    onClick={() => setActiveTab('cadre')}
                >
                    {txt.tabCadre}
                </div>
                <div
                    className={`tab-btn ${activeTab === 'voter' ? 'active text-danger' : 'text-muted'}`}
                    onClick={() => setActiveTab('voter')}
                >
                    {txt.tabVoter}
                </div>
            </div>

            {/* Add Voter Captain Modal - Updated to match AdminUsers styling */}
            {isAddModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '500px', width: '95%', padding: "0px" }}>
                        <div className="modal-header">
                            <h2>{txt.addModalTitle}</h2>
                            <button className="close-btn-icon" onClick={() => { setIsAddModalOpen(false); setAddFormData({ name: '', mobile_no: '', start_voter_no: '', end_voter_no: '' }); setAddMobileError(""); }}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleAddVoterCaptain}>
                            <div className="modal-body-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '10px 0' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.nameLabel}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }}
                                        required
                                        value={addFormData.name}
                                        onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.mobileLabel}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        style={{
                                            padding: '10px 15px',
                                            borderRadius: '8px',
                                            border: addMobileError ? '1px solid #ef4444' : '1px solid #d1d5db',
                                            outline: 'none'
                                        }}
                                        required
                                        maxLength="10"
                                        value={addFormData.mobile_no}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                            const limitedValue = numericValue.slice(0, 10);
                                            setAddFormData({ ...addFormData, mobile_no: limitedValue });
                                            if (addMobileError) setAddMobileError("");
                                            if (limitedValue.length === 10) {
                                                const validation = validateMobileNumber(limitedValue);
                                                if (!validation.isValid) {
                                                    setAddMobileError(validation.message);
                                                } else {
                                                    setAddMobileError("");
                                                }
                                            } else if (limitedValue.length > 0 && limitedValue.length < 10) {
                                                setAddMobileError(txt.mobileLength);
                                            }
                                        }}
                                    />
                                    {addMobileError && (
                                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                            {addMobileError}
                                        </div>
                                    )}
                                </div>

                                {/* ✅ Start & End Voter No in ONE ROW */}
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.startVoterLabel}</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                            required
                                            value={addFormData.start_voter_no}
                                            onChange={(e) => setAddFormData({ ...addFormData, start_voter_no: e.target.value.replace(/[^0-9]/g, '') })}
                                        />
                                    </div>
                                    <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                        <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.endVoterLabel}</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                            required
                                            value={addFormData.end_voter_no}
                                            onChange={(e) => setAddFormData({ ...addFormData, end_voter_no: e.target.value.replace(/[^0-9]/g, '') })}
                                        />
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" className="btn-cancel" onClick={() => { setIsAddModalOpen(false); setAddFormData({ name: '', mobile_no: '', start_voter_no: '', end_voter_no: '' }); setAddMobileError(""); }} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>
                                    {txt.cancelBtn}
                                </button>
                                <button type="submit" className="btn-search-main" disabled={isSubmitting} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                                    {isSubmitting ? txt.addingBtn : txt.submitBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Mobile Number Edit Modal */}
            {isEditMobileOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setIsEditMobileOpen(false)}>
                    <div className="slide-up-element" style={{ background: 'white', padding: '24px', borderRadius: '16px', width: '90%', maxWidth: '350px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
                        <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>{txt.editMobileTitle}</h4>
                        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>{editingVoter?.eng_f_name} {editingVoter?.eng_surname}</p>

                        <div style={{ marginBottom: '24px' }}>
                            <input
                                type="tel"
                                maxLength="10"
                                value={newMobile}
                                onChange={(e) => setNewMobile(e.target.value.replace(/[^0-9]/g, ''))}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: (newMobile && !/^[6-9]\d{9}$/.test(newMobile) && newMobile.length === 10) ? '1px solid #ef4444' : '1px solid #cbd5e1',
                                    borderRadius: '10px',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                placeholder={txt.mobilePlaceholder}
                                autoFocus
                            />
                            {newMobile && newMobile.length > 0 && !/^[6-9]\d{9}$/.test(newMobile) && (
                                <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px', fontWeight: '500' }}>
                                    {newMobile.length < 10 ? txt.mobileErrorLength : txt.mobileErrorStart}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => setIsEditMobileOpen(false)}
                                style={{ padding: '10px 16px', background: 'white', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}
                            >
                                {txt.cancel}
                            </button>
                            <button
                                onClick={handleUpdateMobile}
                                disabled={isSavingMobile || !/^[6-9]\d{9}$/.test(newMobile)}
                                style={{
                                    padding: '10px 20px',
                                    background: (!/^[6-9]\d{9}$/.test(newMobile)) ? '#94a3b8' : '#2563eb',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    cursor: (!/^[6-9]\d{9}$/.test(newMobile)) ? 'not-allowed' : 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {isSavingMobile ? txt.saving : txt.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Captain Modal - Updated to match AdminUsers styling */}
            {isEditModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content-box" style={{ maxWidth: '500px', width: '95%', padding: "0px" }}>
                        <div className="modal-header">
                            <h2>{txt.editModalTitle}</h2>
                            <button className="close-btn-icon" onClick={() => setIsEditModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleUpdateCaptain}>
                            <div className="modal-body-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '10px 0' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.nameLabel}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={editFormData.name}
                                        onChange={handleEditInputChange}
                                        className="form-input"
                                        style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none' }}
                                        required
                                    />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.mobileLabel}</label>
                                    <input
                                        type="text"
                                        name="mobile_no"
                                        value={editFormData.mobile_no}
                                        onChange={handleEditInputChange}
                                        maxLength="10"
                                        className="form-input"
                                        style={{
                                            padding: '10px 15px',
                                            borderRadius: '8px',
                                            border: editMobileError ? '1px solid #ef4444' : '1px solid #d1d5db',
                                            outline: 'none'
                                        }}
                                        required
                                    />
                                    {editMobileError && (
                                        <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                                            {editMobileError}
                                        </div>
                                    )}
                                </div>

                                {/* ✅ Start & End Voter No in ONE ROW — only for VC type */}
                                {editFormData.user_type === 'VC' && (
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.startVoterLabel}</label>
                                            <input
                                                type="text"
                                                name="start_voter_no"
                                                value={editFormData.start_voter_no}
                                                onChange={handleEditInputChange}
                                                className="form-input"
                                                style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                        <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>{txt.endVoterLabel}</label>
                                            <input
                                                type="text"
                                                name="end_voter_no"
                                                value={editFormData.end_voter_no}
                                                onChange={handleEditInputChange}
                                                className="form-input"
                                                style={{ padding: '10px 15px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                            />
                                        </div>
                                    </div>
                                )}

                            </div>
                            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" className="btn-cancel" onClick={() => setIsEditModalOpen(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>
                                    {txt.cancelBtn}
                                </button>
                                <button type="submit" className="btn-search-main" disabled={isEditing} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#2563eb', color: 'white', cursor: isEditing ? 'not-allowed' : 'pointer' }}>
                                    {isEditing ? txt.updatingBtn : txt.updateBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Profile Detail Modal */}
            {selectedCaptain && isDetailModalOpen && (
                <div className="modal-overlay" onClick={closeDetailModal}>
                    <div className="profile-detail-container" onClick={(e) => e.stopPropagation()}>
                        <button className="profile-close-btn" onClick={closeDetailModal}>
                            <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="profile-detail-card">
                            <div className="profile-avatar-wrapper">
                                <div className="profile-avatar-bg">
                                    <img
                                        src={selectedCaptain.photo_path || selectedCaptain.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCaptain.name)}&background=e5e5e5&color=a3a3a3`}
                                        alt={selectedCaptain.name}
                                    />
                                </div>
                            </div>
                            <div className="profile-content">
                                <h2 className="profile-name">{selectedCaptain.name}</h2>
                                <p className="profile-mobile">{selectedCaptain.mobile_no}</p>

                                {/* Show voter range for VC */}
                                {selectedCaptain.user_type === 'VC' && selectedCaptain.start_voter_no && (
                                    <div style={{ marginBottom: '16px', fontSize: '13px', color: '#475569' }}>
                                        📊 Voters: {selectedCaptain.start_voter_no} - {selectedCaptain.end_voter_no}
                                    </div>
                                )}

                                <div className="profile-big-btns">
                                    <a href={`https://wa.me/91${selectedCaptain.mobile_no}?text=${encodeURIComponent(generateWhatsAppMessage(selectedCaptain.name, selectedCaptain.user_type))}`} target="_blank" rel="noreferrer" className="btn-resp btn-wa">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                        </svg>
                                        {txt.sendWhatsapp}
                                    </a>
                                    <a href={`sms:${selectedCaptain.mobile_no}?body=${encodeURIComponent(generateSMSMessage(selectedCaptain.name, selectedCaptain.user_type))}`} className="btn-resp btn-sms-big">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2 2z"></path>
                                        </svg>
                                        {txt.sendSMS}
                                    </a>
                                </div>
                            </div>
                            <div className="profile-bottom-bar">
                                <a href={`tel:${selectedCaptain.mobile_no}`} className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#0ea5e9' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                                        </svg>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <a href={`https://wa.me/91${selectedCaptain.mobile_no}?text=${encodeURIComponent(generateWhatsAppMessage(selectedCaptain.name, selectedCaptain.user_type))}`} target="_blank" rel="noreferrer" className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#22c55e' }}>
                                        <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                        </svg>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <a href={`sms:${selectedCaptain.mobile_no}?body=${encodeURIComponent(generateSMSMessage(selectedCaptain.name, selectedCaptain.user_type))}`} className="btm-icon-wrapper">
                                    <div className="icon-transparent" style={{ color: '#3b82f6', position: 'relative' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 10H6v-2h12v2zm0-3H6V7h12v2z" />
                                        </svg>
                                    </div>
                                </a>
                                <div className="divider"></div>
                                <button className="btm-icon-wrapper" onClick={handleEditClick} style={{ background: 'none', border: 'none', padding: 0 }}>
                                    <div className="icon-transparent" style={{ color: '#f59e0b' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                                        </svg>
                                    </div>
                                </button>
                                <div className="divider"></div>
                                <button className="btm-icon-wrapper" onClick={() => handleDeleteCaptain(selectedCaptain.user_id || selectedCaptain.id)} style={{ background: 'none', border: 'none', padding: 0 }}>
                                    <div className="icon-transparent" style={{ color: '#ef4444' }}>
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-container p-3 p-md-4 mx-auto">
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted">Loading...</p>
                    </div>
                ) : (
                    <>
                        {/* --- SANGATHAN (CADRE) TAB --- */}
                        {activeTab === 'cadre' && (
                            <div className="slide-up-element">

                                {/* Booth Captains (Gold Cards) */}
                                <div className="row g-4 mb-4 mt-2">
                                    {boothCaptains.map((bc, index) => (
                                        <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={bc.user_id || index}>
                                            <div className="gold-card h-100">
                                                <div className="d-flex align-items-center w-100">
                                                    <div className="avatar-circle me-3" onClick={() => openDetailModal(bc)} style={{ cursor: 'pointer' }}>
                                                        {bc.photo_path ? (
                                                            <img src={bc.photo_path} alt={bc.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                        ) : (
                                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="#1e293b">
                                                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="voter-list-header d-flex justify-content-between align-items-start">
                                                            <h3 className="captain-name text-dark mb-1">{bc.name}</h3>
                                                            <button className="menu-btn" onClick={(e) => openDetailModal(bc, e)}>
                                                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                                    <circle cx="5" cy="12" r="2" />
                                                                    <circle cx="12" cy="12" r="2" />
                                                                    <circle cx="19" cy="12" r="2" />
                                                                </svg>
                                                            </button>
                                                        </div>

                                                        <div className="voter-list-header d-flex justify-content-between align-items-center mt-2">
                                                            <div>
                                                                <span className="designation-badge">{txt.boothCaptain}</span>
                                                                <div className="phone-text mt-1">📞 {bc.mobile_no}</div>
                                                            </div>
                                                            <div className="voter-badge">
                                                                {txt.voters} {bc.voters_allocated || 0}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Voter Captains Section */}
                                <div className="voter-list-header d-flex justify-content-between align-items-center mb-3 mt-4 flex-wrap gap-2">
                                    <h5 className="font-weight-bold mb-0 text-dark">{txt.voterCaptain}</h5>
                                    <button className="add-vc-btn" onClick={() => setIsAddModalOpen(true)}>{txt.addVoterCaptain}</button>
                                </div>

                                <div className="row g-4">
                                    {voterCaptains.length > 0 ? voterCaptains.map((vc, index) => (
                                        <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={vc.user_id || index}>
                                            <div className="white-card h-100" onClick={() => navigate('/dis_voter_capation_wise_voter', { state: { userId: vc.user_id } })}>
                                                <div className="voter-list-header d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap">
                                                    <div className="d-flex align-items-center mb-2 mb-sm-0">
                                                        <div className="avatar-initial-circle me-3" onClick={(e) => { e.stopPropagation(); openDetailModal(vc, e); }} style={{ cursor: 'pointer' }}>
                                                            {vc.photo_path ? (
                                                                <img src={vc.photo_path} alt={vc.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                            ) : (
                                                                vc.name ? vc.name.charAt(0).toUpperCase() : 'V'
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h6 className="font-weight-bold mb-0 text-dark">{index + 1}. {vc.name}</h6>
                                                            <small className="text-muted">{vc.mobile_no}</small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button className="action-icon-btn" onClick={(e) => { e.stopPropagation(); window.open(`tel:${vc.mobile_no}`); }}>📞</button>
                                                        <button className="action-icon-btn" onClick={(e) => { e.stopPropagation(); openDetailModal(vc, e); }}>•••</button>
                                                    </div>
                                                </div>

                                                <div className="stats-row">
                                                    <div className="stat-col border-right">
                                                        <span className="stat-num">{vc.start_voter_no || '-'}</span>
                                                        <span className="stat-lbl">{txt.voterFrom}</span>
                                                    </div>
                                                    <div className="stat-col border-right">
                                                        <span className="stat-num">{vc.end_voter_no || '-'}</span>
                                                        <span className="stat-lbl">{txt.voterTo}</span>
                                                    </div>
                                                    <div className="stat-col">
                                                        <span className="stat-num">{vc.voters_allocated || '0'}</span>
                                                        <span className="stat-lbl">{txt.totalVoters}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="col-12">
                                            <div className="text-center text-muted p-4 bg-white rounded shadow-sm border">
                                                {txt.noData}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* --- VOTER TAB (Styled Cards with Pagination & Search) --- */}
                        {activeTab === 'voter' && (
                            <div className="slide-up-element">
                                <div className="voter-list-header d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
                                    <h5 className="font-weight-bold mb-0">{txt.voters} List ({searchedVoterList.length})</h5>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {/* 🔴 NEW: Search Input for Voters */}
                                        <div className="voter-search-wrapper">
                                            <svg className="search-icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                            <input
                                                className="voter-search-input"
                                                type="text"
                                                placeholder={txt.searchVoter}
                                                value={voterSearchTerm}
                                                onChange={(e) => { setVoterSearchTerm(e.target.value); setCurrentPage(1); }}
                                            />
                                            {voterSearchTerm && (
                                                <button
                                                    className="clear-search-btn-small"
                                                    onClick={() => { setVoterSearchTerm(""); setCurrentPage(1); }}
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>

                                        {/* Filter Dropdown Button */}
                                        <div className="filter-dropdown-container">
                                            <button
                                                className="filter-btn"
                                                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                                            >
                                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
                                                </svg>
                                                {getFilterButtonText()}
                                                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>

                                            {isFilterDropdownOpen && (
                                                <div className="filter-dropdown-menu">
                                                    <button
                                                        className={`filter-option ${filterStatus === 'all' ? 'active' : ''}`}
                                                        onClick={() => { setFilterStatus('all'); setIsFilterDropdownOpen(false); }}
                                                    >
                                                        {txt.filterAll}
                                                    </button>
                                                    <button
                                                        className={`filter-option ${filterStatus === 'available' ? 'active' : ''}`}
                                                        onClick={() => { setFilterStatus('available'); setIsFilterDropdownOpen(false); }}
                                                    >
                                                        <span className="filter-dot available-dot"></span>
                                                        {txt.filterAvailable}
                                                    </button>
                                                    <button
                                                        className={`filter-option ${filterStatus === 'not_available' ? 'active' : ''}`}
                                                        onClick={() => { setFilterStatus('not_available'); setIsFilterDropdownOpen(false); }}
                                                    >
                                                        <span className="filter-dot not-available-dot"></span>
                                                        {txt.filterNotAvailable}
                                                    </button>
                                                    <button
                                                        className={`filter-option ${filterStatus === 'pending' ? 'active' : ''}`}
                                                        onClick={() => { setFilterStatus('pending'); setIsFilterDropdownOpen(false); }}
                                                    >
                                                        <span className="filter-dot pending-dot"></span>
                                                        {txt.filterPending}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4 pb-5">
                                    {currentSearchedVoters.length > 0 ? currentSearchedVoters.map((voter, index) => (
                                        <div className="col-12 col-md-6 col-lg-4" key={voter.id || index}>
                                            <div className="voter-card-clean h-100">

                                                {/* Header with Conditional Badge/Button */}
                                                <div className="voter-header d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <span className="serial-number">{indexOfFirstItem + index + 1}.</span>
                                                        <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                                    </div>
                                                    <div>
                                                        {voter.voter_available === null && (
                                                            <button
                                                                className="btn-add-survey"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate('/voter_survey_form', { state: { voter: voter } });
                                                                }}
                                                            >
                                                                + {txt.addSurvey}
                                                            </button>
                                                        )}
                                                        {voter.voter_available === true && (
                                                            <span
                                                                className={`badge-status ${getStatusColorClass(voter.voter_status)}`}
                                                            >
                                                                {getStatusFullForm(voter.voter_status)}
                                                            </span>
                                                        )}
                                                        {voter.voter_available === false && (
                                                            <span className="badge-status status-negative">
                                                                {voter.not_available_reason || 'Not Available'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="voter-body position-relative">
                                                    <div
                                                        className="map-icon-wrapper"
                                                        style={{ cursor: 'pointer', zIndex: 10 }}
                                                        title="View on Google Maps"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const address = voter.eng_localityid;
                                                            if (address && address !== "N/A") {
                                                                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                                                            } else {
                                                                alert("Address not available!");
                                                            }
                                                        }}
                                                    >
                                                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                                    </div>
                                                    <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                                    <div className="info-row"><span className="label">{txt.address}</span><span className="value">{voter.eng_localityid || "N/A"}</span></div>
                                                    <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart}</span></div>
                                                    <div className="info-row" style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span className="label">{txt.mobile}</span>
                                                        <span className="value mobile-value" style={{ flexGrow: 1 }}>{voter.contact_no || "N/A"}</span>
                                                        <svg
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingVoter(voter);
                                                                setNewMobile(voter.contact_no || '');
                                                                setIsEditMobileOpen(true);
                                                            }}
                                                            style={{ cursor: 'pointer', color: '#f59e0b', width: '18px', height: '18px', flexShrink: 0 }}
                                                            fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </div>
                                                    <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                                    <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || "N/A"}</span></div>
                                                    <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                                    <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                                </div>

                                                <div className="voter-actions">
                                                    <ActionButton icon="📞" color="#0ea5e9" label={txt.call}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!voter.contact_no || voter.contact_no === "N/A" || voter.contact_no === "-" || voter.contact_no === "") {
                                                                alert("Mobile number is not available for this voter.");
                                                            }
                                                            else {
                                                                window.open(`tel:${voter.contact_no}`, '_blank');
                                                            }
                                                        }}

                                                    />
                                                    <ActionButton icon="✉️" color="#f59e0b" label={txt.text} onClick={() => handleSmsShare(voter)} />
                                                    {/* નવો કોડ */}
                                                    <WhatsappSlipButton voter={voter} />
                                                    {/* નોંધ: જો map માં વેરીએબલ નું નામ member હોય, તો voter={member} પાસ કરજો */}
                                                    <ActionButton
                                                        icon="🖨"
                                                        color="#f97316"
                                                        label={txt.print}
                                                        onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                                    />
                                                    <PracharButton voter={voter} />
                                                    <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} onClick={() => navigate('/dis_family_member', { state: { voter_id: voter.id } })} />
                                                </div>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="col-12">
                                            <div className="text-center text-muted p-4 bg-white rounded shadow-sm border">
                                                {voterSearchTerm ? `No voters found matching "${voterSearchTerm}"` : txt.noData}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination Component */}
                                {!loading && searchedVoterList.length > itemsPerPage && (
                                    <div className="pagination-wrapper">
                                        <div className="pagination-content">
                                            <button
                                                className="pagination-nav-btn"
                                                onClick={() => paginate(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <div className="page-numbers">
                                                {getPageNumbers().map((number, idx) => (
                                                    <React.Fragment key={idx}>
                                                        {number === '...' ? (
                                                            <span className="page-dots">...</span>
                                                        ) : (
                                                            <button
                                                                className={`page-num-btn ${currentPage === number ? 'active' : ''}`}
                                                                onClick={() => paginate(number)}
                                                            >
                                                                {number}
                                                            </button>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                            <button
                                                className="pagination-nav-btn"
                                                onClick={() => paginate(currentPage + 1)}
                                                disabled={currentPage === searchedTotalPages}
                                            >
                                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            <style jsx>{`
                .booth-detail-container { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 60px; }
                .header-wrapper { background: white; padding: 0px 24px; position: sticky; top: 0; z-index: 50;}
                
                /* Tabs */
                .tabs-container { display: flex; background: white; position: sticky; top: 60px; z-index: 40; border-bottom: 1px solid #e2e8f0; }
                .tab-btn { flex: 1; text-align: center; padding: 14px 0; font-weight: 700; font-size: 15px; cursor: pointer; border-bottom: 3px solid transparent; transition: all 0.2s; }
                .tab-btn.active { border-bottom-color: #ef4444; color: #ef4444 !important; }

                /* Common Flex Alignment Helper */
                .voter-list-header { width: 100%; }

                /* 🔴 NEW: Voter Search Styles */
                .voter-search-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0 12px;
                    min-width: 200px;
                }
                .search-icon-small {
                    width: 16px;
                    height: 16px;
                    color: #94a3b8;
                    flex-shrink: 0;
                }
                .voter-search-input {
                    border: none;
                    padding: 8px 10px;
                    font-size: 13px;
                    color: #1e293b;
                    outline: none;
                    width: 100%;
                    background: transparent;
                }
                .voter-search-input::placeholder {
                    color: #94a3b8;
                }
                .clear-search-btn-small {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 12px;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .clear-search-btn-small:hover {
                    color: #ef4444;
                }

                /* Filter Dropdown Styles */
                .filter-dropdown-container { 
                    position: relative; 
                }
                .filter-btn {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 12px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                }
                .filter-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
                .filter-dropdown-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 8px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                    min-width: 160px;
                    z-index: 100;
                    overflow: hidden;
                }
                .filter-option {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    width: 100%;
                    padding: 10px 16px;
                    border: none;
                    background: white;
                    font-size: 13px;
                    font-weight: 500;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    text-align: left;
                }
                .filter-option:hover { background: #f1f5f9; }
                .filter-option.active { background: #eff6ff; color: #2563eb; }
                .filter-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
                .available-dot { background-color: #22c55e; }
                .not-available-dot { background-color: #ef4444; }
                .pending-dot { background-color: #f59e0b; }

                /* 🔴 NEW: Dynamic Voter Status Colors */
                .status-green { background-color: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
                .status-red { background-color: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }
                .status-orange { background-color: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
                .status-blue { background-color: #eff6ff; color: #1e3a8a; border: 1px solid #bfdbfe; }

                /* Add Survey Button */
                .btn-add-survey {
                    background-color: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 16px;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    white-space: nowrap;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }
                .btn-add-survey:hover { background-color: #2563eb; transform: translateY(-1px); }

                /* Status Badges */
                .badge-status {
                    display: inline-block;
                    border-radius: 16px;
                    font-size: 11px;
                    font-weight: 600;
                    padding: 4px 12px;
                    white-space: nowrap;
                    display: inline-flex;
                    align-items: center;
                }
                .status-negative { background-color: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

                /* Modal Styles */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; box-sizing: border-box; }
                .modal-content-box { background: white; border-radius: 12px; width: 100%; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1); box-sizing: border-box; }
                .modal-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding: 15px; margin-bottom: 10px; }
                .modal-header h2 { margin: 0; font-size: 1.25rem; color: #111827; }
                .close-btn-icon { background: none; border: none; cursor: pointer; color: #6b7280; }
                .close-btn-icon:hover { color: #111827; }

                /* Gold Booth Captain Card */
                .gold-card {
                    background: linear-gradient(135deg, #dfbc6d 0%, #c49942 100%);
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 10px rgba(196, 153, 66, 0.3);
                }
                .avatar-circle {
                    width: 70px;
                    height: 70px;
                    background: rgba(255, 255, 255, 0.2);
                    border: 2px solid rgba(255,255,255,0.4);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    overflow: hidden;
                    cursor: pointer;
                }
                .avatar-circle img { width: 100%; height: 100%; object-fit: cover; }
                .captain-name { font-size: 1.1rem; font-weight: 800; color: #1e293b; margin-bottom: 0; }
                .menu-btn { background: none; border: none; color: #1e293b; cursor: pointer; padding: 0; }
                .designation-badge { background: #855d14; color: white; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
                .phone-text { font-size: 13px; font-weight: 600; color: #1e293b; }
                .voter-badge { background: #1e293b; color: white; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }

                /* Voter Captain Section */
                .add-vc-btn { background: #1e3a8a; color: white; border: none; padding: 8px 16px; border-radius: 20px; font-size: 12px; font-weight: 600; box-shadow: 0 2px 6px rgba(30,58,138,0.2); cursor: pointer; transition: all 0.2s; }
                .add-vc-btn:hover { background: #1e4a9a; transform: translateY(-1px); }
                .white-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.03); overflow: hidden; cursor: pointer; transition: all 0.2s; }
                .white-card:hover { transform: translateY(-3px); box-shadow: 0 8px 16px rgba(0,0,0,0.08); }
                .avatar-initial-circle {
                    width: 45px;
                    height: 45px;
                    background: #f1f5f9;
                    color: #1e293b;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    font-weight: 800;
                    overflow: hidden;
                    cursor: pointer;
                }
                .avatar-initial-circle img { width: 100%; height: 100%; object-fit: cover; }
                .action-icon-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #475569;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .action-icon-btn:hover { background: #f8fafc; transform: scale(1.05); }
                .stats-row { display: flex; }
                .stat-col { flex: 1; padding: 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
                .border-right { border-right: 1px solid #e2e8f0; }
                .stat-num { font-size: 18px; font-weight: 800; color: #0f172a; line-height: 1.2; }
                .stat-lbl { font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-top: 2px; text-align: center; }
                
                /* Pagination Styles */
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; pointer-events: none; }
                .pagination-content {
                    background: white;
                    border: 1px solid #E2E8F0;
                    border-radius: 50px;
                    padding: 8px 16px;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                    pointer-events: auto;
                }
                .pagination-nav-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: transparent;
                    border: none;
                    color: #2563EB;
                    cursor: pointer;
                }
                .pagination-nav-btn:disabled { color: #CBD5E1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 5px; align-items: center; }
                .page-num-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    border: 1px solid transparent;
                    background: transparent;
                    color: #475569;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                }
                .page-num-btn.active { background-color: #2563EB; color: white; }
                .page-dots { color: #94A3B8; font-weight: bold; padding: 0 4px; }

                /* Profile Detail Modal Styles */
                .profile-detail-container { position: relative; width: 100%; max-width: 380px; margin-top: 50px; }
                .profile-close-btn { position: absolute; top: -40px; right: -10px; background: none; border: none; cursor: pointer; padding: 5px; z-index: 10; color: white; }
                .profile-detail-card { background: white; border-radius: 20px; display: flex; flex-direction: column; overflow: visible; box-shadow: 0 15px 35px rgba(0,0,0,0.2); }
                .profile-avatar-wrapper { display: flex; justify-content: center; margin-top: -55px; }
                .profile-avatar-bg { width: 110px; height: 110px; background: white; border-radius: 50%; padding: 6px; cursor: pointer; position: relative; overflow: hidden; }
                .profile-avatar-bg img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
                .profile-content { padding: 15px 24px 25px 24px; text-align: center; }
                .profile-name { font-size: 22px; font-weight: 800; color: #111827; text-transform: uppercase; margin: 0; }
                .profile-mobile { font-size: 16px; color: #374151; letter-spacing: 0.5px; font-weight: 500; margin: 8px 0; }
                .profile-big-btns { display: flex; flex-direction: column; gap: 12px; }
                .btn-resp {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    width: 100%;
                    padding: 14px;
                    border-radius: 50px;
                    color: white;
                    font-weight: 600;
                    text-decoration: none;
                    font-size: 15px;
                    transition: transform 0.2s, box-shadow 0.2s;
                    border: none;
                    box-sizing: border-box;
                }
                .btn-resp:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.15); color: white; }
                .btn-wa { background-color: #4ade80; }
                .btn-sms-big { background-color: #f59e0b; }
                .profile-bottom-bar { background-color: #213555; border-radius: 0 0 20px 20px; display: flex; justify-content: space-evenly; align-items: center; padding: 12px 0; }
                .divider { width: 1px; height: 24px; background-color: rgba(255, 255, 255, 0.3); }
                .btm-icon-wrapper { background: none; border: none; padding: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: transform 0.2s; text-decoration: none; }
                .btm-icon-wrapper:hover { transform: scale(1.1); }
                .icon-transparent { display: flex; align-items: center; justify-content: center; }

                /* Animations */
                .slide-up-element { animation: slideUp 0.4s ease forwards; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* --- MOBILE RESPONSIVENESS FIXES --- */
                @media (max-width: 768px) {
                    .tabs-container { top: 56px; }
                    
                    .pagination-content { padding: 6px 12px; }
                    .page-num-btn { width: 28px; height: 28px; font-size: 12px; }
                    .pagination-nav-btn { width: 32px; height: 32px; }
                    
                    .profile-detail-container { max-width: 320px; }
                    .profile-avatar-bg { width: 90px; height: 90px; }
                    .profile-name { font-size: 18px; }
                    .profile-content { padding: 15px 16px 20px 16px; }
                    .btn-resp { font-size: 13px; padding: 12px; }
                    .btn-add-survey { font-size: 10px; padding: 4px 10px; }
                    .badge-status { font-size: 10px; padding: 4px 10px; }
                    
                    .voter-search-wrapper { 
                        min-width: 150px; 
                        width: 100%;
                    }
                    .filter-btn { 
                        padding: 6px 10px; 
                        font-size: 11px; 
                        gap: 4px;
                    }
                    .filter-dropdown-menu { 
                        right: auto; 
                        left: 0; 
                        min-width: 140px;
                    }
                    .filter-dropdown-container {
                        align-self: flex-start;
                        margin-top: 8px;
                    }
                    .voter-list-header {
                        flex-direction: column;
                        align-items: stretch !important;
                    }
                    .voter-list-header .d-flex.gap-2 {
                        flex-wrap: wrap;
                    }
                }

                @media (max-width: 576px) {
                    .filter-btn { 
                        padding: 5px 8px; 
                        font-size: 10px;
                    }
                    .filter-btn svg {
                        width: 12px;
                        height: 12px;
                    }
                    .voter-search-wrapper {
                        min-width: 120px;
                    }
                }
            `}</style>
        </div>
    );
};

// Reused ActionButton component
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }

    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default BoothCaptainVoter;